// Shared ArcGIS authentication helper.
//
// SMEPPS uses a server-side token broker (POST /arcgis/get-token) so that all
// SMEPPS users share ONE ArcGIS Portal/Server identity (ARCGIS_API_USERNAME).
// This avoids consuming a named-user seat per browser session.
//
// Usage: anywhere a map needs to read protected layers from
// https://smepps-geo.sabah.gov.my, call:
//
//   import { ensureArcgisAuth } from "../../utils/arcgisAuth";
//   await ensureArcgisAuth();
//
// before initializing the MapView. The first call fetches a token, registers
// a request interceptor that appends `?token=…` to any request hitting the
// SMEPPS GIS server, and refreshes the token in-place ~5 min before expiry.

import esriConfig from "@arcgis/core/config";
import axios from "./axios";

// Broad URL prefix — covers both /arcgis (Portal) and /arcmanager (Server)
// endpoints on the SMEPPS GIS host. The token is harmless on unprotected
// requests, so erring wide is safe.
const SERVER_PREFIX = "https://smepps-geo.sabah.gov.my";
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

let inflight = null;
let currentToken = null;
let expiresMs = 0;
let interceptorRegistered = false;

export async function ensureArcgisAuth() {
  if (Date.now() + REFRESH_BUFFER_MS < expiresMs && currentToken) {
    return;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // Either env var name works — the OAUTH one is preferred (it's the
      // accurate name) but the legacy ARCGIS one is honoured for back-compat.
      const referer =
        process.env.REACT_APP_OAUTH_REDIRECT_URI ||
        process.env.REACT_APP_ARCGIS_REDIRECT_URI;
      const { data } = await axios.post("/arcgis/get-token", { referer });
      const payload = data?.data ?? data;
      if (!payload?.token) {
        throw new Error("Token broker returned no token");
      }

      currentToken = payload.token;
      expiresMs = new Date(payload.expires).getTime();

      if (!interceptorRegistered) {
        esriConfig.request.interceptors.push({
          urls: SERVER_PREFIX,
          before(params) {
            if (!currentToken) return;
            params.requestOptions = params.requestOptions || {};
            params.requestOptions.query = params.requestOptions.query || {};
            // Don't overwrite a token already set by caller code.
            if (!params.requestOptions.query.token) {
              params.requestOptions.query.token = currentToken;
            }
          },
        });
        interceptorRegistered = true;
      }
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
